import React from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaClock, FaSyncAlt } from "react-icons/fa";
import { Spinner } from "src/components/shared/atoms/spinner";
import { useTheme } from "src/context/theme.context";
import { formatTimestamp } from "src/utils/dateHandler.util";
import {
  useGetUserByIdQuery,
  useGetUserSpaceSwitchHistoryQuery,
} from "../../services/user.Service";
import { SpaceType } from "../../types/user-space-switch-history.entity";

const SpaceHistoryEmptyState = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <div
    className={`rounded-[2rem] border border-dashed p-10 text-center ${
      isDarkMode
        ? "border-cyan-800/70 bg-cyan-950/30 text-cyan-50"
        : "border-cyan-200 bg-cyan-50/80 text-slate-900"
    }`}
  >
    <div
      className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl shadow-sm ${
        isDarkMode ? "bg-slate-900/80 ring-1 ring-cyan-800/70" : "bg-white/80"
      }`}
    >
      <FaSyncAlt className={isDarkMode ? "text-cyan-300" : "text-cyan-500"} />
    </div>
    <h3 className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-slate-900"}`}>No switch history found</h3>
    <p className={`mt-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
      No entries were found for this user in `user_spaces_switch_history`.
    </p>
  </div>
);

const SWITCH_COOLDOWN_MS = 48 * 60 * 60 * 1000;

const formatRemainingDuration = (targetDate: Date) => {
  const diffMs = targetDate.getTime() - Date.now();

  if (diffMs <= 0) return "Available now";

  const totalMinutes = Math.ceil(diffMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `In ${days}d ${hours}h`;
  if (hours > 0) return `In ${hours}h ${minutes}m`;
  return `In ${minutes}m`;
};

const getNextAvailableSwitchState = (timestamp?: { seconds: number } | null) => {
  if (!timestamp) {
    return {
      isAvailable: true,
      label: "Available",
      helper: "No previous switch date found",
    };
  }

  const nextAvailableDate = new Date(timestamp.seconds * 1000 + SWITCH_COOLDOWN_MS);

  if (Date.now() >= nextAvailableDate.getTime()) {
    return {
      isAvailable: true,
      label: "Available",
      helper: "Cooldown already passed",
    };
  }

  return {
    isAvailable: false,
    label: formatTimestamp({
      seconds: Math.floor(nextAvailableDate.getTime() / 1000),
    } as never),
    helper: formatRemainingDuration(nextAvailableDate),
  };
};

const SpaceHistoryPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { isDarkMode } = useTheme();

  const { data: user, isLoading: userLoading, isError: userError } = useGetUserByIdQuery(userId || "", {
    skip: !userId,
  });
  const {
    data: history = [],
    isLoading: historyLoading,
    isFetching: historyFetching,
    isError: historyError,
  } = useGetUserSpaceSwitchHistoryQuery(userId || "", {
    skip: !userId,
  });

  if (!userId) {
    return <div className="p-6 text-red-600">User ID not found.</div>;
  }

  if (userLoading || historyLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
          <h2 className="text-2xl font-bold text-red-800">User not found</h2>
          <p className="mt-3 text-red-600">We could not load this user details page.</p>
          <Link
            to="/users"
            className="mt-6 inline-flex rounded-2xl bg-red-600 px-5 py-3 font-semibold text-white no-underline transition-colors hover:bg-red-700 hover:no-underline"
          >
            Back to Users
          </Link>
        </div>
      </div>
    );
  }

  const lastLearningSpaceSwitchingDate = user.appMeta?.lastLearningSpaceSwitchingDate;
  const lastCoachingSpaceSwitchingDate = user.appMeta?.lastCoachingSpaceSwitchingDate;
  const nextLearningSwitch = getNextAvailableSwitchState(lastLearningSpaceSwitchingDate);
  const nextCoachingSwitch = getNextAvailableSwitchState(lastCoachingSpaceSwitchingDate);
  const hasLearnerRole = user.roles?.some((role) => role.toLowerCase().includes("learner"));
  const hasCoachRole = user.roles?.some((role) => role.toLowerCase().includes("coach"));
  const getSpaceTypeBadgeClasses = (spaceType?: SpaceType) => {
    if (spaceType === "COACHING") {
      return isDarkMode
        ? "border-violet-700/70 bg-violet-950/60 text-violet-200"
        : "border-violet-200 bg-violet-50 text-violet-700";
    }

    return isDarkMode
      ? "border-cyan-700/70 bg-cyan-950/60 text-cyan-200"
      : "border-cyan-200 bg-cyan-50 text-cyan-700";
  };

  return (
    <div
      className={`min-h-screen py-8 ${
        isDarkMode
          ? "bg-gradient-to-br from-gray-950 via-slate-950 to-gray-900"
          : "bg-gradient-to-br from-cyan-50 via-white to-sky-50"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4">
        <Link
          to={`/users/${userId}`}
          className={`mb-6 inline-flex items-center gap-3 no-underline transition-colors hover:no-underline ${
            isDarkMode ? "text-emerald-300 hover:text-emerald-200" : "text-emerald-600 hover:text-emerald-700"
          }`}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
            <FaArrowLeft />
          </div>
          <span className="text-lg font-semibold">Back to User Details</span>
        </Link>

        <div
          className={`rounded-[2rem] border p-8 shadow-2xl backdrop-blur ${
            isDarkMode
              ? "border-slate-800/80 bg-slate-950/75"
              : "border-white/70 bg-white/90"
          }`}
        >
          <div>
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-cyan-500 via-sky-500 to-blue-600 text-white shadow-xl">
              <FaSyncAlt className="text-2xl" />
            </div>
            <h1 className={`text-4xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Space Switch History</h1>
            <p className={`mt-3 max-w-2xl text-lg ${isDarkMode ? "text-slate-300" : "text-gray-600"}`}>
              Review space switching activity for{" "}
              <span className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{user.name}</span>.
            </p>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            <div
              className={`rounded-3xl border bg-gradient-to-br p-5 shadow-sm ${
                isDarkMode
                  ? "border-cyan-800/70 from-cyan-950/60 to-slate-950 text-cyan-100"
                  : "border-cyan-200 from-cyan-50 to-white"
              }`}
            >
              <div className={`text-sm font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-cyan-300" : "text-cyan-600"}`}>History Entries</div>
              <div className={`mt-3 text-4xl font-bold ${isDarkMode ? "text-white" : "text-cyan-900"}`}>{history.length}</div>
            </div>

            {hasLearnerRole && (
              <div
                className={`rounded-3xl border bg-gradient-to-br p-5 shadow-sm ${
                  isDarkMode
                    ? "border-sky-800/70 from-sky-950/60 to-slate-950"
                    : "border-sky-200 from-sky-50 to-white"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-500 text-white shadow-lg">
                    <FaClock />
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-sky-300" : "text-sky-600"}`}>
                      User Can Switch Learning Space At
                    </div>
                    <div
                      className={`mt-2 text-lg font-bold ${
                        nextLearningSwitch.isAvailable
                          ? "text-emerald-500"
                          : isDarkMode
                            ? "text-white"
                            : "text-slate-900"
                      }`}
                    >
                      {nextLearningSwitch.label}
                    </div>
                    <div className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                      {nextLearningSwitch.helper}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {hasCoachRole && (
              <div
                className={`rounded-3xl border bg-gradient-to-br p-5 shadow-sm ${
                  isDarkMode
                    ? "border-violet-800/70 from-violet-950/60 to-slate-950"
                    : "border-violet-200 from-violet-50 to-white"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-500 text-white shadow-lg">
                    <FaClock />
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-violet-300" : "text-violet-600"}`}>
                      User Can Switch Coaching Space At
                    </div>
                    <div
                      className={`mt-2 text-lg font-bold ${
                        nextCoachingSwitch.isAvailable
                          ? "text-emerald-500"
                          : isDarkMode
                            ? "text-white"
                            : "text-slate-900"
                      }`}
                    >
                      {nextCoachingSwitch.label}
                    </div>
                    <div className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                      {nextCoachingSwitch.helper}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {historyError && (
            <div
              className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${
                isDarkMode
                  ? "border-amber-800/70 bg-amber-950/40 text-amber-200"
                  : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
            >
              We could not load the full space switch history cleanly, so the list below may be incomplete.
            </div>
          )}

          <div className="mt-10">
            {history.length === 0 ? (
              <SpaceHistoryEmptyState isDarkMode={isDarkMode} />
            ) : (
              <div className="space-y-4">
                {history.map((entry, index) => (
                  <div
                    key={entry.id}
                    className={`rounded-[2rem] border bg-gradient-to-br p-6 shadow-lg ${
                      isDarkMode
                        ? "border-slate-800/80 from-slate-950 via-cyan-950/20 to-slate-900"
                        : "border-cyan-200/80 from-white via-cyan-50/70 to-sky-50"
                    }`}
                  >
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                              isDarkMode
                                ? "border-cyan-800/70 bg-slate-900/80 text-cyan-200"
                                : "border-cyan-200 bg-white/80 text-cyan-700"
                            }`}
                          >
                            Switch #{history.length - index}
                          </div>
                          <div
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${getSpaceTypeBadgeClasses(entry.spaceType)}`}
                          >
                            {entry.spaceType || "LEARNING"}
                          </div>
                        </div>
                        <div className={`mt-3 text-sm font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          Target Space
                        </div>
                        <div
                          className={`mt-1 break-all rounded-2xl px-4 py-3 font-mono text-sm shadow-sm ${
                            isDarkMode
                              ? "bg-slate-900 text-cyan-100 ring-1 ring-cyan-800/60"
                              : "bg-slate-900 text-cyan-50"
                          }`}
                        >
                          {entry.targetSpaceId}
                        </div>
                      </div>

                      <div className="min-w-0 lg:max-w-sm">
                        <div className={`text-sm font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          Previous Space
                        </div>
                        <div
                          className={`mt-1 break-all rounded-2xl px-4 py-3 font-mono text-sm shadow-sm ring-1 ${
                            isDarkMode
                              ? "bg-slate-900/70 text-slate-200 ring-slate-700"
                              : "bg-white/80 text-slate-700 ring-slate-200"
                          }`}
                        >
                          {entry.previousSpaceId || "No previous space recorded"}
                        </div>
                      </div>

                      <div className="lg:text-right">
                        <div className={`text-sm font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          Switched At
                        </div>
                        <div className={`mt-1 text-base font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                          {formatTimestamp(entry.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {historyFetching && (
            <div className={`mt-6 text-sm font-medium ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>
              Refreshing space switch history...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpaceHistoryPage;
